import React, { useCallback, useContext, useEffect, useState } from 'react'

import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import {
  getOrganizationSettings,
  updateOrganizationSettings,
} from '../../utilities/api'

import { PageLoadingContainer } from '../PageLoadingContainer'
import OrganizationFeeConfigCard from './OrganizationFeeConfigCard'

const SETTING_GROUPS = [
  {
    title: 'Features',
    settings: [
      {
        key: 'fundraisingEnabled',
        label: 'Fundraising',
        description: 'Shows fundraising in navigation and settings.',
      },
      {
        key: 'donationsEnabled',
        label: 'Donations',
        description: 'Enables the donations settings section.',
      },
      {
        key: 'foodAndBeverage',
        label: 'Food & beverage',
        description: 'Enables marketplace settings and F&B ordering.',
      },
      {
        key: 'accessPass',
        label: 'Access pass',
        description: 'Enables access pass templates and management.',
      },
      {
        key: 'aiAssistant',
        label: 'AI assistant',
        description: 'Enables AI assistant tools across the dashboard.',
      },
    ],
  },
  {
    title: 'Tickets',
    settings: [
      {
        key: 'allowTransfers',
        label: 'Allow transfers',
        description: 'Lets ticket holders transfer tickets to others.',
      },
      {
        key: 'allowResales',
        label: 'Allow resales',
        description:
          "Turns resale on or off for this organization's current and future events.",
      },
      {
        key: 'automaticTicketReleases',
        label: 'Automatic ticket releases',
        description: 'Automatically releases held tickets on schedule.',
      },
      {
        key: 'addToWallet',
        label: 'Add to wallet',
        description: 'Shows add-to-wallet options on tickets.',
      },
      {
        key: 'printTickets',
        label: 'Print tickets',
        description: 'Enables ticket printing for this organization.',
      },
      {
        key: 'redeemVouchersOnline',
        label: 'Redeem flex tickets online',
        description: 'Lets customers redeem flex ticket codes during checkout.',
      },
      {
        key: 'smsDelivery',
        label: 'SMS delivery',
        description: 'Lets this organization send tickets by text message.',
      },
    ],
  },
  {
    title: 'Billing',
    settings: [
      {
        key: 'automaticInvoices',
        label: 'Automatic invoices',
        description: 'Automatically generates invoices for orders.',
      },
      {
        key: 'paymentPlans',
        label: 'Payment plans',
        description:
          'Enables payment plan assignments and the payment plans report.',
      },
    ],
  },
]

const syncOrgSettingInStorage = (patch) => {
  const orgs = AuthService.getOrg() || []
  if (!orgs.length) return

  const next = orgs.map((org, index) =>
    index === 0
      ? {
          ...org,
          setting: {
            ...(org.setting || {}),
            ...patch,
          },
        }
      : org
  )

  AuthService.setOrg(next)
  window.dispatchEvent(new CustomEvent('blocktickets:org-settings-updated'))
}

export default function OrganizationSettingsWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const organization = AuthService.getOrg()?.[0]
  const organizationUUID = organization?.uuid

  const [settings, setSettings] = useState({})
  const [organizationName, setOrganizationName] = useState(
    organization?.name || ''
  )
  const [savingKey, setSavingKey] = useState(null)
  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })

  const loadSettings = useCallback(async () => {
    if (!organizationUUID) return

    showLoading()
    try {
      const response = await getOrganizationSettings(organizationUUID)
      const payload = response?.data || response
      setSettings(payload?.settings || {})
      setOrganizationName(payload?.organizationName || organization?.name || '')
    } catch (error) {
      console.error(error)
      setAlert({
        show: true,
        variant: 'danger',
        message: 'Unable to load organization settings.',
      })
    } finally {
      hideLoading()
    }
  }, [hideLoading, organization?.name, organizationUUID, showLoading])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleToggle = async (key, enabled) => {
    if (!organizationUUID || savingKey) return

    const previousValue = Boolean(settings[key])
    setSettings((current) => ({ ...current, [key]: enabled }))
    setSavingKey(key)
    setAlert({ show: false, variant: '', message: '' })

    try {
      const response = await updateOrganizationSettings(organizationUUID, {
        [key]: enabled,
      })
      const payload = response?.data || response
      const nextSettings = payload?.settings || { ...settings, [key]: enabled }
      setSettings(nextSettings)
      syncOrgSettingInStorage(nextSettings)
    } catch (error) {
      console.error(error)
      setSettings((current) => ({ ...current, [key]: previousValue }))
      setAlert({
        show: true,
        variant: 'danger',
        message: 'Unable to save that setting. Please try again.',
      })
    } finally {
      setSavingKey(null)
    }
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer />
      ) : (
        <div className="position-relative">
          <section className="wrapper">
            {alert.show && (
              <Alert
                variant={alert.variant}
                className="mb-5"
                onClose={() =>
                  setAlert({ show: false, variant: '', message: '' })
                }
                dismissible
              >
                {alert.message}
              </Alert>
            )}
            <header className="section-header">
              <div className="section-heading section-heading--secondary">
                <h1>Organization settings</h1>
              </div>
              <p className="section-header-desc">
                Enable or disable features for{' '}
                <strong>
                  {organizationName || 'the selected organization'}
                </strong>
                . Changes apply to the organization selected in the header.
              </p>
            </header>

            <OrganizationFeeConfigCard organizationUUID={organizationUUID} />

            {SETTING_GROUPS.map((group) => (
              <Card key={group.title} body className="card--sm mb-4">
                <h2 className="h5 mb-4">{group.title}</h2>
                <div className="d-flex flex-column gap-4">
                  {group.settings.map((setting) => (
                    <div
                      key={setting.key}
                      className="d-flex align-items-start justify-content-between gap-4"
                    >
                      <div>
                        <p className="mb-1 fw-semibold">{setting.label}</p>
                        <small className="subtitle subtitle--dark">
                          {setting.description}
                        </small>
                      </div>
                      <Form.Check
                        type="switch"
                        id={`org-setting-${setting.key}`}
                        checked={Boolean(settings[setting.key])}
                        disabled={Boolean(savingKey)}
                        onChange={(event) =>
                          handleToggle(setting.key, event.target.checked)
                        }
                        aria-label={setting.label}
                        className="mb-0 flex-shrink-0"
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </section>
        </div>
      )}
    </>
  )
}
